// React/finance-app/src/features/user-dashboard/hooks/usePortActivation.js
import { useState, useRef, useCallback, useEffect } from "react";
import { activatePortById } from "../../../api";
import { usePortActivationStatus } from "../../../shared/portActivation";

/**
 * Port activation state manager.
 *
 * Composes usePortActivationStatus (hardware-first server state + poll + websocket)
 * with local reconciliation for countdowns, warmup, guards, and optimistic UI.
 */

/**
 * Extract port group (numeric prefix) from port number
 * e.g., "36D" -> 36, "46A" -> 46
 */
function getPortGroup(portNumber) {
  if (!portNumber) return null;
  const digits = String(portNumber).replace(/\D/g, "");
  return digits ? parseInt(digits, 10) : null;
}

export default function usePortActivation(ports, setPorts, options = {}) {
  const {
    scope = "me",
    gatewayId = null,
    adaptivePoll = true,
  } = options;

  const {
    portStatus: serverPortStatus,
    refresh: refreshActivation,
    loading: activationLoading,
    error: activationError,
  } = usePortActivationStatus({
    scope,
    gatewayId,
    adaptivePoll,
    remapPorts: ports,
    enabled: true,
  });

  const [portStatus, setPortStatus] = useState({});
  // NOTE: these are lightweight, transient notices (not the SMS history box).
  const [portMessages, setPortMessages] = useState({});
  const [portCountdowns, setPortCountdowns] = useState({});
  // Ports currently being smart-polled after activation: Map(portId -> { gatewayId, portNumber })
  const [pollingPorts, setPollingPorts] = useState(new Map());

  const countdownRefs = useRef(new Map());
  const warmupRefs = useRef(new Map());
  // Ref to track current countdown state for use in callbacks
  const countdownStateRef = useRef({});
  // Ref to track when countdowns finish to prevent immediate restarts
  const countdownFinishedAtRef = useRef(new Map());

  const activatePort = activatePortById;

  // == CHANGE 1: logging-enhanced startCountdown ==
  const startCountdown = useCallback((portId, seconds) => {
    if (!seconds || seconds <= 0) {
      console.log(
        `⚠️ Port ${portId}: Attempted to start countdown with invalid seconds: ${seconds}`
      );
      return;
    }

    // Normalize portId to number for consistent key type
    const normalizedPortId = Number(portId);
    if (isNaN(normalizedPortId)) {
      console.error(`⚠️ Port ${portId}: Invalid port ID, cannot start countdown`);
      return;
    }

    // Check if countdown just finished (within last 2 seconds) - prevent immediate restart
    const finishedAt = countdownFinishedAtRef.current.get(normalizedPortId);
    if (finishedAt && Date.now() - finishedAt < 2000) {
      return;
    }

    setPortCountdowns((prev) => {
      const next = { ...prev };
      // Store with normalized number key, but also clean up any string key variant
      next[normalizedPortId] = seconds;
      if (prev[String(portId)] != null && String(portId) !== String(normalizedPortId)) {
        delete next[String(portId)];
      }
      // Update ref for callback access
      countdownStateRef.current = next;
      return next;
    });

    if (countdownRefs.current.has(normalizedPortId)) {
      clearInterval(countdownRefs.current.get(normalizedPortId));
      countdownRefs.current.delete(normalizedPortId);
    }
    
    // Clear the "just finished" flag when starting a new countdown
    countdownFinishedAtRef.current.delete(normalizedPortId);

    const countdownInterval = setInterval(() => {
      setPortCountdowns((prev) => {
        const current = prev[normalizedPortId];

        if (current == null || current <= 1) {
          clearInterval(countdownInterval);
          countdownRefs.current.delete(normalizedPortId);
          // Mark that this countdown just finished (prevent immediate restart for 2 seconds)
          countdownFinishedAtRef.current.set(normalizedPortId, Date.now());

          const next = { ...prev };
          delete next[normalizedPortId];
          // Update ref for callback access
          countdownStateRef.current = next;
          return next;
        }

        const nextValue = current - 1;

        const next = { ...prev, [normalizedPortId]: nextValue };
        // Update ref for callback access
        countdownStateRef.current = next;
        return next;
      });
    }, 1000);

    countdownRefs.current.set(normalizedPortId, countdownInterval);
  }, []);

  const clearCountdown = useCallback((portId) => {
    // Normalize portId to number for consistent key type
    const normalizedPortId = Number(portId);
    if (isNaN(normalizedPortId)) return;
    
    if (countdownRefs.current.has(normalizedPortId)) {
      clearInterval(countdownRefs.current.get(normalizedPortId));
      countdownRefs.current.delete(normalizedPortId);
    }
    // Clear the "just finished" flag when explicitly clearing
    countdownFinishedAtRef.current.delete(normalizedPortId);
    
    setPortCountdowns((prev) => {
      const next = { ...prev };
      // Clean up both number and string key variants
      delete next[normalizedPortId];
      if (String(portId) !== String(normalizedPortId)) {
        delete next[String(portId)];
      }
      // Update ref for callback access
      countdownStateRef.current = next;
      return next;
    });
  }, []);

  const startWarmup = useCallback((portId, seconds = 20) => {
    if (seconds <= 0) return;
    if (warmupRefs.current.has(portId)) return;

    setPortStatus((prev) => {
      const existing = prev[portId] || {};
      return {
        ...prev,
        [portId]: {
          ...existing,
          ui_warmup_until: Date.now() + seconds * 1000,
        },
      };
    });

    const t = setTimeout(() => {
      setPortStatus((prev) => {
        const cur = prev[portId] || {};
        const { ui_warmup_until, ...rest } = cur;
        return { ...prev, [portId]: { ...rest } };
      });
      clearTimeout(t);
      warmupRefs.current.delete(portId);
    }, seconds * 1000);

    warmupRefs.current.set(portId, t);
  }, []);

  const clearWarmup = useCallback((portId) => {
    if (warmupRefs.current.has(portId)) {
      clearTimeout(warmupRefs.current.get(portId));
      warmupRefs.current.delete(portId);
    }
    setPortStatus((prev) => {
      const cur = prev[portId] || {};
      const { ui_warmup_until, ...rest } = cur;
      return { ...prev, [portId]: { ...rest } };
    });
  }, []);

  /** Prevent ACTIVE -> QUEUED/INACTIVE while under guard */
  const startGuard = useCallback((portId, seconds) => {
    if (!seconds || seconds <= 0) return;
    setPortStatus((prev) => {
      const existing = prev[portId] || {};
      const nextUntil = Date.now() + seconds * 1000;
      const until =
        existing.activation_guard_until &&
        existing.activation_guard_until > Date.now()
          ? Math.max(existing.activation_guard_until, nextUntil)
          : nextUntil;
      return {
        ...prev,
        [portId]: {
          ...existing,
          activation_guard_until: until,
        },
      };
    });
  }, []);

  /** Transient row notice (never null) */
  const setPortStatusAndMessage = useCallback(
    (portId, status, message = "", type = "info", eta = null) => {
      setPortStatus((prev) => ({
        ...prev,
        [portId]: { ...prev[portId], status, timestamp: Date.now(), eta },
      }));

      if (message) {
        setPortMessages((prev) => ({
          ...prev,
          [portId]: { message, type, timestamp: Date.now() },
        }));
        // auto-clear after 5s (delete the key instead of null)
        setTimeout(() => {
          setPortMessages((prev) => {
            const next = { ...prev };
            delete next[portId];
            return next;
          });
        }, 5000);
      }

      if (status === "queued" && eta && eta > 0) {
        startCountdown(portId, eta);
      }
    },
    [startCountdown]
  );

  const clearPortMessage = useCallback((portId) => {
    setPortMessages((prev) => {
      const next = { ...prev };
      delete next[portId];
      return next;
    });
  }, []);

  const getGatewayId = useCallback((port) => {
    return port?.gateway_id ?? port?.gateway?.id ?? 1;
  }, []);

  const startActivationPolling = useCallback((portId, gatewayId, portNumber = null) => {
    setPollingPorts((prev) => new Map(prev).set(portId, { gatewayId, portNumber }));
  }, []);

  const stopActivationPolling = useCallback((portId) => {
    setPollingPorts((prev) => {
      const next = new Map(prev);
      next.delete(portId);
      return next;
    });
  }, []);

  /**
   * Core reconciliation with backend activity.
   * - Handover grace: if another port becomes ACTIVE and this one was ACTIVE but set to INACTIVE,
   *   keep it ACTIVE for up to 30s with warning.
   * - Queue pinning: keep QUEUED stable using queue_pin_until.
   * - Guard: ignore downgrades while activation_guard_until is in the future.
   */
  const setPortStatusFromActivity = useCallback(
    (incoming) => {
      setPortStatus((prev) => {
        const now = Date.now();
        const updated = { ...prev };
        // Store reference to prev for same-group port checks
        const prevStatus = prev;
        // Capture current countdown state from ref (updated in real-time by countdown intervals)
        const currentCountdownsSnapshot = { ...countdownStateRef.current };

        Object.entries(incoming).forEach(([pid, data]) => {
          const portId = parseInt(pid, 10);
          const prevEntry = updated[portId] || {};
          const newStatus = data?.status || "inactive";
          const backendRemaining = data?.remaining_seconds || 0;
          const show30 = !!data?.show_30s_warning;
          const isMessageTriggered = !!data?.message_triggered;
          const isHandover = !!data?.handover;

          // Respect activation guard: don't allow ACTIVE -> downgrade while guard is active
          // Also protect handover state (status is "active" but handover flag is set)
          if (
            prevEntry.activation_guard_until &&
            prevEntry.activation_guard_until > now &&
            (prevEntry.status === "active" || prevEntry.handover) &&
            newStatus !== "active" &&
            newStatus !== "handing_over"
          ) {
            // Ignore this downgrade - keep current state
            return;
          }

          // Respect queue pin window: if pinned queued and backend isn't saying ACTIVE, keep queued
          if (
            prevEntry.queue_pin_until &&
            prevEntry.queue_pin_until > now &&
            newStatus !== "active"
          ) {
            // Keep existing queued state & countdown
            return;
          }

          // ACTIVATING (from backend during activation phase)
          if (newStatus === "activating") {
            const effectiveMessageTriggered =
              isMessageTriggered || prevEntry.message_triggered;


            updated[portId] = {
              ...prevEntry,
              status: "active",
              timestamp: now,
              remaining_seconds: backendRemaining,
              show_30s_warning: true,
              message_triggered: effectiveMessageTriggered,
              ui_warmup_until: now + 20000,
            };

            const hasActiveCountdown = countdownRefs.current.has(portId);

            if (!hasActiveCountdown && backendRemaining > 0) {
              startCountdown(portId, backendRemaining);
            }

            return;
          }

          // QUEUED
          if (newStatus === "queued") {
            clearWarmup(portId);
            clearCountdown(portId);

            updated[portId] = {
              ...prevEntry,
              status: "queued",
              timestamp: now,
              remaining_seconds: backendRemaining,
              show_30s_warning: false,
            };

            if (backendRemaining > 0) {
              startCountdown(portId, backendRemaining);
            }
            return;
          }

          // HANDING_OVER (from backend queue logic - same-group port activated)
          if (newStatus === "handing_over") {
            const normalizedPortId = Number(portId);
            // Use snapshot captured at start of callback
            const currentCountdownState = currentCountdownsSnapshot[normalizedPortId] ?? currentCountdownsSnapshot[String(portId)];
            const hasActiveCountdown = countdownRefs.current.has(normalizedPortId);
            

            // Use backend's remaining_seconds or default to 30s
            const handoverSeconds = backendRemaining > 0 ? backendRemaining : 30;

            // Set activation guard to prevent premature downgrade
            startGuard(portId, handoverSeconds);

            // Start countdown if not already running
            if (!hasActiveCountdown) {
              startCountdown(portId, handoverSeconds);
            }

            // Keep port ACTIVE with warning (backend sends "handing_over" but UI shows as active with warning)
            // IMPORTANT: Don't overwrite remaining_seconds if countdown is already running - use actual countdown state
            const finalRemaining = hasActiveCountdown && currentCountdownState != null 
              ? currentCountdownState 
              : handoverSeconds;
            
            updated[portId] = {
              ...prevEntry,
              status: "active", // Display as active in UI
              timestamp: now,
              remaining_seconds: finalRemaining,
              show_30s_warning: true,
              handover: true,
            };
            return;
          }

          // == CHANGE 2: logging-enhanced ACTIVE section ==
          // ACTIVE
          if (newStatus === "active") {
            const wasAlreadyActive = prevEntry.status === "active";
            const hadWarmup =
              prevEntry.ui_warmup_until && prevEntry.ui_warmup_until > now;
            const effectiveMessageTriggered =
              isMessageTriggered || prevEntry.message_triggered;


            updated[portId] = {
              ...prevEntry,
              status: "active",
              timestamp: now,
              remaining_seconds: backendRemaining,
              show_30s_warning:
                show30 ||
                isHandover ||
                (backendRemaining > 0 && backendRemaining <= 30),
              message_triggered: effectiveMessageTriggered,
              handover: isHandover,
            };

            const hasActiveCountdown = countdownRefs.current.has(portId);

            if (!hasActiveCountdown) {
              // Check if we just finished a handover countdown - if so, don't restart it
              const wasInHandover = prevEntry.handover || prevEntry.show_30s_warning;
              const normalizedPortId = Number(portId);
              const actualCountdownState = currentCountdownsSnapshot[normalizedPortId] ?? currentCountdownsSnapshot[String(portId)];
              // Countdown just finished if: was in handover AND (countdown state is 0/missing OR prev remaining <= 0)
              const countdownJustFinished = wasInHandover && (
                (actualCountdownState != null && actualCountdownState <= 0) ||
                (prevEntry.remaining_seconds != null && prevEntry.remaining_seconds <= 0)
              );
              
              if (countdownJustFinished) {
                // Clear handover flags since countdown finished
                updated[portId] = {
                  ...updated[portId],
                  handover: false,
                  show_30s_warning: false,
                  remaining_seconds: undefined,
                };
                clearCountdown(portId);
              } else if (backendRemaining > 0) {
                clearCountdown(portId);
                const seconds =
                  isHandover || show30
                    ? Math.min(30, backendRemaining || 30)
                    : backendRemaining;
                startCountdown(portId, seconds);
              } else if (isHandover || show30) {
                clearCountdown(portId);
                startCountdown(portId, 30);
              } else {
                clearCountdown(portId);
              }
            }

            // Warmup: ONLY for NEW activations that are NOT message-triggered
            const isInitialLoad =
              !prevEntry.timestamp || now - prevEntry.timestamp > 10000;

            if (
              !wasAlreadyActive &&
              !effectiveMessageTriggered &&
              !hadWarmup &&
              !isInitialLoad
            ) {
              clearWarmup(portId);
              startWarmup(portId, 20);
            } else if (effectiveMessageTriggered || isInitialLoad) {
              clearWarmup(portId);
            }

            return;
          }

          // INACTIVE
          if (newStatus === "inactive") {
            // Check if we're in a handover state - if so, preserve it until countdown finishes
            const isInHandover = prevEntry.handover || prevEntry.show_30s_warning;
            const normalizedPortId = Number(portId);
            const hasActiveCountdown = countdownRefs.current.has(normalizedPortId);
            
            // Get countdown value from previous entry's remaining_seconds or use guard time
            const countdownValue = prevEntry.remaining_seconds;
            const guardActive = prevEntry.activation_guard_until && prevEntry.activation_guard_until > now;
            
            // Protect handover ONLY if countdown is still actively running
            // If countdown has finished (no active ref), allow transition to inactive even if guard is still active
            // This prevents infinite countdown loops when the handover period naturally expires
            if (isInHandover && hasActiveCountdown) {
              // Countdown is still running - preserve handover state
              // Calculate remaining time: use countdown value if > 0, otherwise use guard time, otherwise default to 1
              let remaining = countdownValue;
              if (remaining == null || remaining <= 0) {
                if (guardActive) {
                  remaining = Math.max(1, Math.floor((prevEntry.activation_guard_until - now) / 1000));
                } else {
                  // Countdown is running but value is 0 or missing - use a small buffer
                  remaining = 1;
                }
              }
              
              // IMPORTANT: Use the actual countdown state if available, otherwise use calculated remaining
              const actualCountdownState = currentCountdownsSnapshot[normalizedPortId] ?? currentCountdownsSnapshot[String(portId)];
              // This prevents overwriting the countdown with stale remaining_seconds values
              const finalRemaining = (actualCountdownState != null && actualCountdownState > 0)
                ? actualCountdownState
                : (countdownValue != null && countdownValue > 0) 
                  ? countdownValue 
                  : remaining;
              
              // If countdown has actually finished (value is 0 or negative), allow transition to inactive
              if (actualCountdownState != null && actualCountdownState <= 0) {
                // Don't protect - let it transition to inactive
                // Fall through to the inactive handler below
              } else {
                // Keep the active state with handover flag
                updated[portId] = {
                  ...prevEntry,
                  status: "active",
                  timestamp: now,
                  show_30s_warning: true,
                  handover: true,
                  remaining_seconds: finalRemaining,
                };
                return;
              }
            }
            
            // Countdown has finished or never started - allow transition to inactive
            // Clear any remaining handover state
            clearCountdown(portId);
            clearWarmup(portId);

            updated[portId] = {
              ...prevEntry,
              status: "inactive",
              timestamp: now,
              remaining_seconds: undefined,
              show_30s_warning: false,
              message_triggered: false,
              handover: false,
            };
            return;
          }

          // Fallback
          updated[portId] = {
            ...prevEntry,
            ...data,
            timestamp: now,
          };
        });

        return updated;
      });
    },
    // == CHANGE 3: dependency array WITHOUT portCountdowns (prevents loops) ==
    // Include ports to check for same-group port activations
    [clearCountdown, clearWarmup, startWarmup, startCountdown, ports]
  );

  const reconcileFromServerRef = useRef(() => {});
  reconcileFromServerRef.current = setPortStatusFromActivity;

  useEffect(() => {
    if (!serverPortStatus || Object.keys(serverPortStatus).length === 0) return;
    reconcileFromServerRef.current(serverPortStatus);
  }, [serverPortStatus]);

  /** Triggered by the user pressing Activate */
  const onActivatePort = useCallback(
    async (portId, phoneNumber) => {
      try {
        setPortStatus((prev) => ({
          ...prev,
          [portId]: {
            ...prev[portId],
            status: "activating",
            timestamp: Date.now(),
          },
        }));
        // IMPORTANT: never set to null; set a small notice instead (or skip writing).
        setPortMessages((prev) => ({
          ...prev,
          [portId]: {
            message: "Starting activation…",
            type: "info",
            timestamp: Date.now(),
          },
        }));

        const response = await activatePort(portId, phoneNumber);

        if (response.status === "queued" || response.status === "activating") {
          const eta = response.eta_seconds || response.remaining_seconds || 60;
          const pinUntil = Date.now() + (eta || 30) * 1000;
          setPortStatus((prev) => ({
            ...prev,
            [portId]: {
              ...prev[portId],
              status: response.status === "activating" ? "activating" : "queued",
              timestamp: Date.now(),
              remaining_seconds: eta,
              queue_pin_until: pinUntil,
            },
          }));
          startCountdown(portId, eta);
          setTimeout(() => clearPortMessage(portId), 3000);
          const port = ports.find((p) => p.id === portId);
          const gatewayId = getGatewayId(port);
          const portNumber = port?.port_number ?? null;
          startActivationPolling(portId, gatewayId, portNumber);
        } else if (response.status === "active") {
          const rem = response.remaining_seconds || 60;
          setPortStatus((prev) => ({
            ...prev,
            [portId]: {
              ...prev[portId],
              status: "active",
              timestamp: Date.now(),
              remaining_seconds: rem,
              queue_pin_until: undefined,
            },
          }));
          startGuard(portId, rem || 60);
          startWarmup(portId, 20);
          startCountdown(portId, rem);
          setTimeout(() => clearPortMessage(portId), 3000);
        }
      } catch (err) {
        console.error("Activation error:", err);
        setPortStatus((prev) => ({
          ...prev,
          [portId]: {
            ...prev[portId],
            status: "error",
            timestamp: Date.now(),
          },
        }));
        setPortMessages((prev) => ({
          ...prev,
          [portId]: {
            message:
              err?.response?.data?.detail ||
              err?.message ||
              "Activation failed",
            type: "danger",
            timestamp: Date.now(),
          },
        }));
      }
    },
    [startCountdown, activatePort, startWarmup, startGuard, clearPortMessage, ports, getGatewayId, startActivationPolling]
  );

  useEffect(() => {
    return () => {
      countdownRefs.current.forEach((interval) => clearInterval(interval));
      countdownRefs.current.clear();
      warmupRefs.current.forEach((t) => clearTimeout(t));
      warmupRefs.current.clear();
    };
  }, []);

  return {
    portStatus,
    portMessages,
    portCountdowns,
    setPortStatusAndMessage,
    clearPortMessage,
    onActivatePort,
    setPortStatusFromActivity,
    startCountdown,
    clearCountdown,
    pollingPorts,
    startActivationPolling,
    stopActivationPolling,
    refreshActivation,
    activationLoading,
    activationError,
  };
}
